import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Drawer,
  Descriptions,
  Tabs,
  InputNumber,
  Row,
  Col,
  Divider,
  Typography
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  EyeOutlined,
  StopOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { accountAPI, compactParams } from '../services/api';

const { Search } = Input;
const { TextArea } = Input;
const { Text } = Typography;

const renderTableStack = (primary, secondary, meta) => (
  <div className="table-cell">
    <div className="table-cell-primary">{primary}</div>
    {secondary ? <div className="table-cell-secondary">{secondary}</div> : null}
    {meta ? <div className="table-cell-meta">{meta}</div> : null}
  </div>
);

const renderEmptyPanel = (title, subtitle) => (
  <div className="empty-state-panel">
    <div className="empty-state-title">{title}</div>
    {subtitle ? <div className="empty-state-subtitle">{subtitle}</div> : null}
  </div>
);

const formatNumber = (value, fallback = '0') => (
  Number.isFinite(Number(value)) ? Number(value).toLocaleString() : fallback
);

const formatTime = (value, { seconds = false } = {}) => {
  if (!value) {
    return '-';
  }

  const date = seconds ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('zh-CN');
};

function AccountManagement() {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    keyword: '',
    minLevel: undefined,
    maxLevel: undefined,
    minPower: undefined,
    maxPower: undefined
  });
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [editVisible, setEditVisible] = useState(false);
  const [banVisible, setBanVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [banForm] = Form.useForm();
  const { current: pageCurrent, pageSize } = pagination;

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = compactParams({
        page: pageCurrent,
        limit: pageSize,
        ...filters
      });

      const response = await accountAPI.getList(params);
      setAccounts(response.data.data.list);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.total
      }));
    } catch (error) {
      console.error('Load accounts error:', error);
      message.error('加载游戏账号列表失败');
    } finally {
      setLoading(false);
    }
  }, [filters, pageCurrent, pageSize]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, keyword: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await accountAPI.getDetail(record.id);
      setCurrentAccount(response.data.data);
      setDetailVisible(true);
    } catch (error) {
      console.error('Get account detail error:', error);
      message.error('加载账号详情失败');
    }
  };

  const handleEdit = (record) => {
    setCurrentAccount(record);
    editForm.setFieldsValue({
      level: record.level,
      power: record.power,
      vip_lv: record.vip_lv,
      stamina: record.stamina,
      food: record.food,
      oil: record.oil,
      iron: record.iron,
      rareEarth: record.rareEarth,
      gold: record.gold,
      diamond: record.diamond
    });
    setEditVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      await accountAPI.update(currentAccount.id, values);
      message.success('更新账号成功');
      setEditVisible(false);
      editForm.resetFields();
      loadAccounts();
    } catch (error) {
      console.error('Update account error:', error);
      message.error('更新账号失败');
    }
  };

  const handleBan = (record) => {
    setCurrentAccount(record);
    banForm.resetFields();
    setBanVisible(true);
  };

  const handleBanSubmit = async (values) => {
    try {
      await accountAPI.ban(currentAccount.id, {
        status: values.status,
        reason: values.reason,
        duration: values.duration
      });
      message.success(values.status === 'banned' ? '封禁成功' : '解封成功');
      setBanVisible(false);
      banForm.resetFields();
      loadAccounts();
    } catch (error) {
      console.error('Ban account error:', error);
      message.error('操作失败');
    }
  };

  const columns = [
    {
      title: '账号 ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (text) => renderTableStack(<span className="table-cell-mono">{text}</span>, '系统账号标识')
    },
    {
      title: '账号名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (text, record) => renderTableStack(text || '未命名账号', record.agent_name || '无代理归属', record.invite_code ? `邀请码 ${record.invite_code}` : null)
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        if (status === 1 || status === 'active') {
          return <Tag color="success">正常</Tag>;
        } else if (status === 2 || status === 'banned') {
          return <Tag color="error">封禁</Tag>;
        }
        return <Tag color="default">未知</Tag>;
      }
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (level) => renderTableStack(<Tag color="blue">Lv.{level}</Tag>, '角色等级')
    },
    {
      title: '战力',
      dataIndex: 'power',
      key: 'power',
      width: 100,
      render: (power) => renderTableStack(<span style={{ color: '#f50' }}>{formatNumber(power)}</span>, '当前战斗力')
    },
    {
      title: 'VIP 等级',
      dataIndex: 'vip_lv',
      key: 'vip_lv',
      width: 80,
      render: (vip) => renderTableStack(<Tag color="gold">VIP{vip}</Tag>, '付费等级')
    },
    {
      title: '所属代理',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 150,
      render: (agent_name, record) => (
        agent_name
          ? renderTableStack(<Tag color="purple">{agent_name}</Tag>, record.invite_code || '无邀请码')
          : <Tag color="default">无</Tag>
      )
    },
    {
      title: '体力',
      dataIndex: 'stamina',
      key: 'stamina',
      width: 90,
      render: (value) => renderTableStack(formatNumber(value), '当前体力')
    },
    {
      title: '资源',
      key: 'resources',
      width: 200,
      render: (_, record) => renderTableStack(
        `粮 ${formatNumber(record.food)}`,
        `油 ${formatNumber(record.oil)} / 铁 ${formatNumber(record.iron)}`,
        `稀土 ${formatNumber(record.rareEarth)}`
      )
    },
    {
      title: '注册时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 160,
      render: (time) => renderTableStack(formatTime(time, { seconds: true }), '账号注册时间')
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space direction="vertical" size={8}>
          <div className="action-group">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            >
              详情
            </Button>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              编辑
            </Button>
          </div>
          <div className="action-group">
            {record.status === 2 || record.status === 'banned' ? (
              <Button
                type="link"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleBan(record)}
              >
                解封
              </Button>
            ) : (
              <Button
                type="link"
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={() => handleBan(record)}
              >
                封禁
              </Button>
            )}
          </div>
        </Space>
      )
    }
  ];

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title">账号管理</div>
          <div className="page-subtitle">按账号、等级和战力快速筛选，常用操作都直接保留在列表内。</div>
        </div>
        <div className="page-header-actions">
          <Tag color="blue">当前页 {accounts.length} 项</Tag>
          <Tag>总计 {pagination.total || 0}</Tag>
        </div>
      </div>

      <div className="page-note">
        <div className="page-note-text">
          <span className="page-note-strong">账号页更偏运营排查。</span>
          先按等级、战力筛掉低价值账号，再进详情查看资源、建筑和登录情况。
        </div>
        <Text type="secondary">当前总记录 {pagination.total || 0}</Text>
      </div>

      <Card className="filter-card" title="筛选条件">
        <div className="filter-bar-row">
          <div className="filter-bar-item filter-bar-search">
            <Search
              placeholder="搜索账号 ID 或账号名称"
              allowClear
              onSearch={handleSearch}
              prefix={<SearchOutlined />}
              style={{ width: '100%' }}
            />
          </div>
          <div className="filter-bar-item">
            <Text type="secondary" className="filter-bar-label">最小等级</Text>
            <InputNumber
              placeholder="最小等级"
              style={{ width: 120 }}
              value={filters.minLevel}
              onChange={(value) => setFilters(prev => ({ ...prev, minLevel: value }))}
            />
          </div>
          <div className="filter-bar-item">
            <Text type="secondary" className="filter-bar-label">最大等级</Text>
            <InputNumber
              placeholder="最大等级"
              style={{ width: 120 }}
              value={filters.maxLevel}
              onChange={(value) => setFilters(prev => ({ ...prev, maxLevel: value }))}
            />
          </div>
          <div className="filter-bar-item">
            <Text type="secondary" className="filter-bar-label">最小战力</Text>
            <InputNumber
              placeholder="最小战力"
              style={{ width: 120 }}
              value={filters.minPower}
              onChange={(value) => setFilters(prev => ({ ...prev, minPower: value }))}
            />
          </div>
          <div className="filter-bar-item">
            <Text type="secondary" className="filter-bar-label">最大战力</Text>
            <InputNumber
              placeholder="最大战力"
              style={{ width: 120 }}
              value={filters.maxPower}
              onChange={(value) => setFilters(prev => ({ ...prev, maxPower: value }))}
            />
          </div>
        </div>
      </Card>>

      <div className="compact-stat-row">
        <div className="compact-stat">
          <div className="compact-stat-label">列表账号数</div>
          <div className="compact-stat-value">{accounts.length}</div>
        </div>
        <div className="compact-stat">
          <div className="compact-stat-label">封禁账号</div>
          <div className="compact-stat-value">{accounts.filter((item) => item.status === 2 || item.status === 'banned').length}</div>
        </div>
        <div className="compact-stat">
          <div className="compact-stat-label">高等级账号</div>
          <div className="compact-stat-value">{accounts.filter((item) => Number(item.level) >= 30).length}</div>
        </div>
        <div className="compact-stat">
          <div className="compact-stat-label">高战力账号</div>
          <div className="compact-stat-value">{accounts.filter((item) => Number(item.power) >= 100000).length}</div>
        </div>
      </div>

      <Card className="table-card" title="账号列表" extra={<Text type="secondary">支持详情、编辑和封禁操作</Text>}>
        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: renderEmptyPanel('暂无账号数据', '请调整搜索词、等级或战力范围后重试。') }}
          pagination={{
            ...pagination,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
            }
          }}
        />
      </Card>

      {/* 账号详情弹窗 */}
      <Drawer
        title="账号详情"
        placement="right"
        width={900}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {currentAccount && currentAccount.player && (
          <Tabs>
            <Tabs.TabPane tab="基本信息" key="basic">
              <div className="detail-section">
                <div className="section-note">
                  <div className="section-note-text">
                    <span className="section-note-strong">{currentAccount.player.name || '当前账号'}</span>
                    当前详情包含基础属性、资源、建筑和登录日志，方便一次性核对账号状态。
                  </div>
                  <Tag color={currentAccount.player.status === 2 || currentAccount.player.status === 'banned' ? 'red' : 'green'}>
                    {currentAccount.player.status === 2 || currentAccount.player.status === 'banned' ? '已封禁' : '状态正常'}
                  </Tag>
                </div>
                <div className="detail-stat-grid">
                  <div className="detail-stat-card">
                    <div className="detail-stat-label">等级</div>
                    <div className="detail-stat-value">Lv.{currentAccount.player.level || 0}</div>
                  </div>
                  <div className="detail-stat-card">
                    <div className="detail-stat-label">战力</div>
                    <div className="detail-stat-value" style={{ color: '#ea580c' }}>{formatNumber(currentAccount.player.power)}</div>
                  </div>
                  <div className="detail-stat-card">
                    <div className="detail-stat-label">VIP</div>
                    <div className="detail-stat-value" style={{ color: '#ca8a04' }}>VIP{currentAccount.player.vip_lv || 0}</div>
                  </div>
                  <div className="detail-stat-card">
                    <div className="detail-stat-label">体力</div>
                    <div className="detail-stat-value">{formatNumber(currentAccount.player.stamina)}</div>
                  </div>
                </div>
                <Descriptions column={2} bordered>
                <Descriptions.Item label="账号 ID">{currentAccount.player.id}</Descriptions.Item>
                <Descriptions.Item label="账号名称">{currentAccount.player.name}</Descriptions.Item>
                <Descriptions.Item label="等级">
                  <Tag color="blue">Lv.{currentAccount.player.level}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="战力">
                  <span style={{ color: '#f50' }}>{currentAccount.player.power?.toLocaleString()}</span>
                </Descriptions.Item>
                <Descriptions.Item label="VIP 等级">
                  <Tag color="gold">VIP{currentAccount.player.vip_lv}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="体力">{currentAccount.player.stamina}</Descriptions.Item>
                <Descriptions.Item label="注册时间">
                  {new Date(currentAccount.player.create_time * 1000).toLocaleString('zh-CN')}
                </Descriptions.Item>
                <Descriptions.Item label="最后登录">
                  {formatTime(currentAccount.player.last_login_time, { seconds: true })}
                </Descriptions.Item>
                </Descriptions>
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="资源信息" key="resources">
              {currentAccount.resources ? (
                <div className="detail-section">
                  <div className="detail-stat-grid">
                    <div className="detail-stat-card">
                      <div className="detail-stat-label">食物</div>
                      <div className="detail-stat-value">{formatNumber(currentAccount.resources.food)}</div>
                    </div>
                    <div className="detail-stat-card">
                      <div className="detail-stat-label">油料</div>
                      <div className="detail-stat-value">{formatNumber(currentAccount.resources.oil)}</div>
                    </div>
                    <div className="detail-stat-card">
                      <div className="detail-stat-label">钢铁</div>
                      <div className="detail-stat-value">{formatNumber(currentAccount.resources.iron)}</div>
                    </div>
                    <div className="detail-stat-card">
                      <div className="detail-stat-label">稀土</div>
                      <div className="detail-stat-value">{formatNumber(currentAccount.resources.rare_earth)}</div>
                    </div>
                  </div>
                  <Descriptions column={2} bordered>
                  <Descriptions.Item label="食物">{currentAccount.resources.food?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="油料">{currentAccount.resources.oil?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="钢铁">{currentAccount.resources.iron?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="稀土">{currentAccount.resources.rare_earth?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="金币">{currentAccount.resources.gold?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="钻石">{currentAccount.resources.diamond?.toLocaleString()}</Descriptions.Item>
                  </Descriptions>
                </div>
              ) : renderEmptyPanel('暂无资源数据', '当前账号没有返回资源详情。')}
            </Tabs.TabPane>

            <Tabs.TabPane tab="建筑信息" key="buildings">
              {currentAccount.buildings && currentAccount.buildings.length > 0 ? (
                <Table
                  dataSource={currentAccount.buildings.slice(0, 20)}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: renderEmptyPanel('暂无建筑数据', '当前账号没有可展示的建筑记录。') }}
                  columns={[
                    { title: '建筑 ID', dataIndex: 'id', key: 'id', width: 100 },
                    { title: '建筑类型', dataIndex: 'type', key: 'type', width: 150 },
                    { title: '等级', dataIndex: 'level', key: 'level', width: 80 },
                    { title: '建造时间', dataIndex: 'build_time', key: 'build_time', width: 180,
                      render: (time) => time ? new Date(time * 1000).toLocaleString('zh-CN') : '-' }
                  ]}
                />
              ) : renderEmptyPanel('暂无建筑数据', '当前账号没有可展示的建筑记录。')}
            </Tabs.TabPane>

            <Tabs.TabPane tab="登录日志" key="loginLogs">
              {currentAccount.loginLogs && currentAccount.loginLogs.length > 0 ? (
                <Table
                  dataSource={currentAccount.loginLogs}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: renderEmptyPanel('暂无登录记录', '当前账号还没有返回可展示的登录流水。') }}
                  columns={[
                    { title: '登录时间', dataIndex: 'login_time', key: 'login_time', width: 180,
                      render: (time) => new Date(time).toLocaleString('zh-CN') },
                    { title: 'IP 地址', dataIndex: 'ip', key: 'ip', width: 150 },
                    { title: '登录方式', dataIndex: 'login_type', key: 'login_type', width: 100 }
                  ]}
                />
              ) : renderEmptyPanel('暂无登录记录', '当前账号还没有返回可展示的登录流水。')}
            </Tabs.TabPane>
          </Tabs>
        )}
      </Drawer>

      {/* 编辑账号弹窗 */}
      <Modal
        title="编辑账号"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={() => editForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <div className="page-note" style={{ marginTop: 0, marginBottom: 16 }}>
            <div className="page-note-text">
              <span className="page-note-strong">直接修改账号的核心属性和基础资源。</span>
              这里适合处理简单纠偏，复杂排查建议先在详情抽屉核对完整数据后再改。
            </div>
          </div>
          <Divider orientation="left">基础信息</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="level" label="等级">
                <InputNumber min={1} max={100} style={{ width: '100%' }} placeholder="请输入等级" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="power" label="战力">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入战力" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="vip_lv" label="VIP 等级">
                <InputNumber min={0} max={15} style={{ width: '100%' }} placeholder="请输入 VIP 等级" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="stamina" label="体力">
                <InputNumber min={0} max={1000} style={{ width: '100%' }} placeholder="请输入体力值" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">资源信息</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="food" label="食物">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入食物数量" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="oil" label="油料">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入油料数量" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="iron" label="钢铁">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入钢铁数量" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rareEarth" label="稀土">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入稀土数量" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="gold" label="金币">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入金币数量" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="diamond" label="钻石">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入钻石数量" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 封禁/解封弹窗 */}
      <Modal
        title={banForm.getFieldValue('status') === 'banned' ? '封禁账号' : '解封账号'}
        open={banVisible}
        onCancel={() => setBanVisible(false)}
        onOk={() => banForm.submit()}
        destroyOnClose
      >
        <Form form={banForm} layout="vertical" onFinish={handleBanSubmit}>
          <div className="page-note" style={{ marginTop: 0, marginBottom: 16 }}>
            <div className="page-note-text">
              <span className="page-note-strong">封禁会直接影响账号登录和使用。</span>
              如无明确原因，优先先查看登录日志和详情数据，避免误操作。
            </div>
          </div>
          <Form.Item
            name="status"
            label="操作类型"
            initialValue="banned"
            rules={[{ required: true }]}
          >
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.status !== curr.status}>
              {({ getFieldValue }) => (
                <div className="action-group">
                  <Button
                    type={getFieldValue('status') === 'banned' ? 'primary' : 'default'}
                    danger={getFieldValue('status') === 'banned'}
                    onClick={() => banForm.setFieldValue('status', 'banned')}
                  >
                    封禁账号
                  </Button>
                  <Button
                    type={getFieldValue('status') === 'active' ? 'primary' : 'default'}
                    onClick={() => banForm.setFieldValue('status', 'active')}
                  >
                    解除封禁
                  </Button>
                </div>
              )}
            </Form.Item>
          </Form.Item>
          {banForm.getFieldValue('status') === 'banned' && (
            <>
              <div className="section-note" style={{ marginBottom: 16 }}>
                <div className="section-note-text">
                  <span className="section-note-strong">封禁时建议填写原因和时长。</span>
                  不填时长即视为长期封禁，后续排查时更容易混淆。
                </div>
              </div>
              <Form.Item name="reason" label="封禁原因">
                <TextArea rows={3} placeholder="请输入封禁原因" />
              </Form.Item>
              <Form.Item name="duration" label="封禁时长（小时）">
                <InputNumber min={1} style={{ width: '100%' }} placeholder="不填表示永久封禁" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default AccountManagement;
